/**
 * Intranet Osasco Plaza — src/modules/shared.jsx
 * ─────────────────────────────────────────────────────────────────────────
 * Camada de base: constantes, helpers puros (formatação, máscara, validação)
 * e átomos de UI sem estado de navegação (StatusPill, Icon, Carousel,
 * TimeSelect, CheckboxGroup, Blocked, PhotoPlaceholder). Nenhum destes
 * depende de nenhum outro módulo — é intencional: evita import circular
 * entre este arquivo e os módulos de domínio, que TODOS importam daqui.
 * Gerado pela quebra de app.jsx (ver DOC/ADR/0011).
 * ─────────────────────────────────────────────────────────────────────────
 */


/* ════════════════════════════════════════════════════════════════
   SHARED COMPONENTS
   ════════════════════════════════════════════════════════════════ */

const { useState, useEffect, useRef } = React;

export const _PERMS_FALLBACK = {
  admin: {
    home:      { view:true, create:true, edit:true, delete:true, approve:true },
    feed:      { view:true, create:true, edit:true, delete:true, approve:true },
    servicos:  { view:true, create:true, edit:true, delete:true, approve:true },
    usuarios:  { view:true, create:true, edit:true, delete:true, approve:true },
    vagas:     { view:true, create:true, edit:true, delete:true, approve:true },
    curriculos:{ view:true, create:true, edit:true, delete:true, approve:true },
    painel:    { view:true, create:true, edit:true, delete:true, approve:true },
    gis:       { view:true, create:true, edit:true, delete:true, approve:true },
    'gis-admin':{ view:true, create:true, edit:true, delete:true, approve:true },
    seguranca:  { view:true, create:true, edit:true, delete:true, approve:true },
  },
  gestor: {
    home:      { view:true, create:false, edit:false, delete:false, approve:false },
    feed:      { view:true, create:true,  edit:true,  delete:false, approve:false },
    servicos:  { view:true, create:true,  edit:true,  delete:false, approve:true },
    usuarios:  { view:true, create:false, edit:false, delete:false, approve:false },
    vagas:     { view:true, create:true,  edit:true,  delete:false, approve:true },
    curriculos:{ view:true, create:false, edit:false, delete:false, approve:false },
    painel:    { view:true, create:true,  edit:true,  delete:false, approve:true },
    gis:       { view:true, create:true,  edit:true,  delete:false, approve:true },
    'gis-admin':{ view:true, create:true, edit:true,  delete:false, approve:true },
    seguranca:  { view:true, create:true, edit:true,  delete:false, approve:true },
  },
  colaborador: {
    home:      { view:true,  create:false, edit:false, delete:false, approve:false },
    feed:      { view:true,  create:false, edit:false, delete:false, approve:false },
    servicos:  { view:true,  create:true,  edit:false, delete:false, approve:false },
    usuarios:  { view:false, create:false, edit:false, delete:false, approve:false },
    vagas:     { view:true,  create:true,  edit:true,  delete:false, approve:false },
    curriculos:{ view:false, create:false, edit:false, delete:false, approve:false },
    painel:    { view:true,  create:true,  edit:false, delete:false, approve:false },
    gis:       { view:true,  create:true,  edit:true,  delete:false, approve:false },
    'gis-admin':{ view:false, create:false, edit:false, delete:false, approve:false },
    seguranca:  { view:true,  create:true, edit:false, delete:false, approve:false },
  },
};


/* Fonte de verdade: PERMS (shared/permissoes.js). O fallback só entra se o
   script não tiver carregado. */
export const DEFAULT_PERMISSIONS = (typeof PERMS !== 'undefined' && PERMS.PADRAO_PERFIL) || _PERMS_FALLBACK;

export const MODULOS_CATALOGO   = (typeof PERMS !== 'undefined' && PERMS.MODULOS) || [];

export const ACOES_CATALOGO     = (typeof PERMS !== 'undefined' && PERMS.ACOES) || ['view','create','edit','delete','approve'];

export const ACAO_LABEL         = (typeof PERMS !== 'undefined' && PERMS.ACAO_LABEL) || { view:'Ver', create:'Criar', edit:'Editar', delete:'Excluir', approve:'Aprovar' };


/**
 * A pergunta que toda tela faz antes de mostrar um botão.
 *
 * IMPORTANTE: isto é experiência de uso, NÃO segurança. Esconder o botão não
 * impede ninguém de montar a chamada na mão — quem barra de verdade é o
 * Worker, que faz a mesma pergunta com o mesmo arquivo de regras. Se você
 * adicionar uma ação nova aqui, adicione a checagem no Worker também.
 */
export function hasPerm(user, mod, action) {
  if (!user) return false;
  if (typeof PERMS !== 'undefined' && PERMS.podeFazer) {
    /* O usuário vem do servidor com `permissoesEfetivas` já resolvidas; o
       adaptador as coloca em `permissions`. */
    return PERMS.podeFazer({ perfil: user.role, permissoes: user.permissionsRaw ?? null }, mod, action);
  }
  const perms = user.permissions || _PERMS_FALLBACK[user.role] || _PERMS_FALLBACK.colaborador;
  return !!(perms[mod] && perms[mod][action]);
}


/* ═══════════════════════════════════════════════════════════
   AUTENTICAÇÃO — servidor, não navegador
   ═══════════════════════════════════════════════════════════

   O protótipo comparava SHA-256 da senha contra uma lista de usuários que
   morava no localStorage. Três problemas de uma vez: (1) a lista inteira de
   hashes ficava visível no DevTools de qualquer máquina; (2) SHA-256 sem sal
   e sem iterações cai em ataque de dicionário em minutos; (3) o bloqueio por
   tentativas era um objeto em memória — F5 zerava.

   Agora quem valida senha é o auth-worker, contra o D1. O navegador recebe
   um JWT e mais nada. O freio de força bruta vive no servidor e devolve 429.
*/

/* Traduz o usuário do servidor para o formato que as telas já esperam.
   Existe para não ter que renomear `role`/`store`/`permissions` em ~40 pontos
   do app. Quando os módulos forem migrados, esta função some. */
export function adaptarUsuario(u, loja) {
  if (!u) return null;
  return {
    id:                  u.id,
    login:               u.login,
    nome:                u.nome,
    role:                u.perfil,
    cargo:               u.cargo || '',
    store:               loja ? loja.nome : (u.loja_nome || ''),
    loja_id:             u.loja_id || null,
    email:               u.email || '',
    telefone:            u.telefone || '',
    /* `permissions` = efetivas (já resolvidas pelo servidor), usadas para
       exibir. `permissionsRaw` = o JSON personalizado gravado, ou null —
       é o que a tela de edição precisa para saber se está personalizado. */
    permissions:         u.permissoesEfetivas || u.permissoes || null,
    permissionsRaw:      u.permissoes || null,
    forcePasswordChange: !!u.senhaTemporaria,
    active:              u.ativo !== false,
    ultimoLoginEm:       u.ultimoLoginEm || null,
  };
}


/* Devolve o usuário adaptado, ou { __blocked, secs } quando o servidor
   respondeu 429, ou { __erro } para qualquer outra falha com mensagem. */
export async function findUserByLogin(login, password) {
  try {
    const d = await API.login(login, password);
    return adaptarUsuario(d.user, d.loja);
  } catch (e) {
    if (e.status === 429) return { __blocked: true, secs: e.retryApos || 60 };
    if (e.status === 401) return null;              // usuário ou senha errados
    return { __erro: e.message || 'Falha ao entrar.' };
  }
}


/* nextRequestId() e updateRequestStatus() foram removidos: solicitação vive
   no D1 agora.

   O contador local era um problema silencioso — morava no localStorage de
   cada navegador, então duas pessoas abrindo um chamado ao mesmo tempo
   recebiam o MESMO número, e ninguém percebia porque cada uma via só o
   próprio banco. Agora a numeração é atômica, numa única instrução SQL.

   O status também saiu: quem valida a transição é o Worker, que recusa pular
   de "Pendente" direto para "Concluído" e recusa mexer no que já foi
   encerrado. */

/* ── CPF validator ── */
export function validarCPF(raw) {
  const d = raw.replace(/\D/g,'');
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  let s = 0;
  for (let i=0; i<9; i++) s += parseInt(d[i]) * (10-i);
  let r = (s*10) % 11; if (r===10||r===11) r=0;
  if (r !== parseInt(d[9])) return false;
  s = 0;
  for (let i=0; i<10; i++) s += parseInt(d[i]) * (11-i);
  r = (s*10) % 11; if (r===10||r===11) r=0;
  return r === parseInt(d[10]);
}


/* ── Sensitive request fields ── */
export const SENSITIVE_REQ_FIELDS = ['cpf','nomeProfissional','nomeEmpresa','telefone'];


/* addRequest()/decryptRequest() removidos: quem grava é API.solicitacoes.criar().

   Vale registrar por que a criptografia daqui não fez falta. Os campos do
   prestador eram cifrados em AES-GCM com uma chave GERADA PELO NAVEGADOR e
   guardada no localStorage, ao lado do texto cifrado. Isso não protegia de
   nada — quem lia o dado lia a chave — e ainda quebrava o compartilhamento:
   como a chave era por navegador, os dados sincronizados pelo GitHub
   apareciam como "***" para todo mundo, menos para quem criou o registro.

   No D1 esses campos ficam legíveis para o banco, e a proteção passa a ser
   autorização de verdade: a listagem nunca devolve dados do prestador, o
   detalhe só devolve para o autor, para o gestor da mesma loja e para o
   admin, e todo acesso vai para `auditoria`.

   O mapa de `owner` (setor responsável) também saiu daqui. Ele ficava
   incompleto no cliente — "marketing" e "rh_financeiro" caíam em "Triagem
   central" porque o mapa nunca foi atualizado quando esses tipos nasceram.
   Agora é SETOR_POR_TIPO no Worker, com uma entrada por tipo. */

/* nextVagaId()/addVaga()/decryptVaga()/decryptAllVagas()/addCurriculo()/
   decryptCv()/SENSITIVE_CV_FIELDS removidos na Fase 5: quem grava e lê agora
   é API.vagas/API.curriculos, contra o worker `intranet-osasco-recrutamento`
   (D1 + R2). O numerador local (`DB.counters.vagaSeq`) tinha o mesmo defeito
   já corrigido em Solicitações e GIS — duas vagas publicadas ao mesmo tempo,
   por lojistas diferentes, podiam nascer com o MESMO número; agora é
   `contadores` no D1, atômico. Ver DOC/ADR/0008. */

/* Aceita epoch (dados antigos do localStorage) E string ISO (vinda do D1).
   Sem essa conversão, `Date.now() - "2026-08-08T…"` vira NaN e a tela mostra
   "há NaN min" — falha feia e silenciosa. */
export function relativeTime(ts) {
  if (!ts) return '';
  if (typeof ts === 'string') { const t = Date.parse(ts); if (isNaN(t)) return ''; ts = t; }
  const diff=Date.now()-ts, m=Math.round(diff/60000);
  if(m<1) return 'agora'; if(m<60) return `há ${m} min`;
  const h=Math.round(m/60); if(h<24) return `há ${h}h`;
  const d=Math.round(h/24); if(d<30) return `há ${d} dia${d>1?'s':''}`;
  return new Date(ts).toLocaleDateString('pt-BR');
}


export const serviceTypes = [
  { id:'maintenance', label:'Manutenção',              icon:'wrench',     desc:'Reparos, infraestrutura, elétrica' },
  { id:'cleaning',    label:'Limpeza',                 icon:'broom',      desc:'Solicitações de limpeza extra' },
  { id:'security',    label:'Segurança',               icon:'shield',     desc:'Acesso, vigilância, ocorrências' },
  { id:'it',          label:'TI & Sistemas',           icon:'bolt',       desc:'Equipamentos, sistemas, redes' },
  { id:'logistics',   label:'Logística',               icon:'truck',      desc:'Entregas, doca, fornecedores' },
  { id:'marketing',   label:'Marketing',               icon:'megaphone',  desc:'Solicitações e outros - Marketing' },
  { id:'rh_financeiro', label:'RH / Financeiro',       icon:'briefcase',  desc:'Solicitações e outros - RH/Financeiro' },
  { id:'other',       label:'Outro',                   icon:'lightbulb',  desc:'Solicitações diversas' },
];

export const navItems = [
  { id:'home',             icon:'home',      label:'Início',               section:'principal' },
  { id:'feed',             icon:'megaphone', label:'Comunicados',          section:'principal' },
  { id:'mini-comunicado',  icon:'bell',      label:'Mini Comunicado',      section:'principal', sub:true },
  { id:'newreq',           icon:'plus',      label:'Nova solicitação',     section:'principal' },
  { id:'mine',             icon:'list',      label:'Meus pedidos',         section:'principal' },
  { id:'usuarios',         icon:'shield',    label:'Usuários',             section:'principal' },
  { id:'lojas',            icon:'briefcase', label:'Lojas',                section:'principal' },
  { id:'vagas',            icon:'briefcase', label:'Cadastrar Vaga',       section:'rh' },
  { id:'curriculos',       icon:'user',      label:'Consultar Currículos', section:'rh' },
  { id:'vagas-lista',      icon:'search',    label:'Consultar Vagas',      section:'painel' },
  { id:'candidatar',       icon:'send',      label:'Candidatar a Vaga',    section:'painel' },
  { id:'gis',              icon:'chart-bar', label:'GIS · Vendas Diárias', section:'gis' },
  { id:'gis-admin',        icon:'key',       label:'GIS - Auditoria',      section:'gis', sub:true },
  { id:'seg-ativos',        icon:'shield',    label:'Ativos',               section:'seguranca' },
  { id:'seg-grupos',        icon:'user',      label:'Grupos de Controle',   section:'seguranca', sub:true },
  { id:'seg-tipos',         icon:'list',      label:'Tipos de Ativo',       section:'seguranca', sub:true },
  { id:'seg-vistorias',     icon:'check-circle', label:'Nova Vistoria',     section:'seguranca' },
  { id:'seg-dashboard',     icon:'chart-bar', label:'Dashboard',            section:'seguranca', sub:true },
];


/* ════════════════════════════════════════════════════════════════
   ICON COMPONENT
   ════════════════════════════════════════════════════════════════ */
export const Icon = ({ name, size=18, stroke=1.6, style }) => {
  const p = { width:size, height:size, viewBox:'0 0 24 24', fill:'none', stroke:'currentColor', strokeWidth:stroke, strokeLinecap:'round', strokeLinejoin:'round', style };
  switch(name) {
    case 'home':        return <svg {...p}><path d="M3 11l9-8 9 8"/><path d="M5 10v10h14V10"/></svg>;
    case 'megaphone':   return <svg {...p}><path d="M3 11v2a2 2 0 0 0 2 2h2l5 4V5L7 9H5a2 2 0 0 0-2 2z"/><path d="M16 8a5 5 0 0 1 0 8"/></svg>;
    case 'plus':        return <svg {...p}><path d="M12 5v14M5 12h14"/></svg>;
    case 'list':        return <svg {...p}><path d="M8 6h12M8 12h12M8 18h12"/><circle cx="4" cy="6" r="1"/><circle cx="4" cy="12" r="1"/><circle cx="4" cy="18" r="1"/></svg>;
    case 'bell':        return <svg {...p}><path d="M6 8a6 6 0 1 1 12 0c0 7 3 7 3 9H3c0-2 3-2 3-9z"/><path d="M10 21a2 2 0 0 0 4 0"/></svg>;
    case 'search':      return <svg {...p}><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>;
    case 'user':        return <svg {...p}><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/></svg>;
    case 'settings':    return <svg {...p}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9c.3.6.9 1 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"/></svg>;
    case 'arrow-right': return <svg {...p}><path d="M5 12h14M13 5l7 7-7 7"/></svg>;
    case 'arrow-left':  return <svg {...p}><path d="M19 12H5M11 5l-7 7 7 7"/></svg>;
    case 'arrow-up':    return <svg {...p}><path d="M12 19V5M5 12l7-7 7 7"/></svg>;
    case 'check':       return <svg {...p}><path d="M5 13l4 4L19 7"/></svg>;
    case 'check-circle':return <svg {...p}><circle cx="12" cy="12" r="9"/><path d="m8 12 3 3 5-6"/></svg>;
    case 'x':           return <svg {...p}><path d="M6 6l12 12M18 6 6 18"/></svg>;
    case 'clock':       return <svg {...p}><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>;
    case 'paperclip':   return <svg {...p}><path d="M21 12 12.5 20.5a5 5 0 0 1-7-7L14 5a3.5 3.5 0 0 1 5 5L10.5 18.5a2 2 0 0 1-3-3L15 8"/></svg>;
    case 'calendar':    return <svg {...p}><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/></svg>;
    case 'tools':       return <svg {...p}><path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2-2 2.5-2.5z"/></svg>;
    case 'bolt':        return <svg {...p}><path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z"/></svg>;
    case 'shield':      return <svg {...p}><path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6l-8-3z"/></svg>;
    case 'sparkle':     return <svg {...p}><path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8"/></svg>;
    case 'heart':       return <svg {...p}><path d="M20.8 7.2a5 5 0 0 0-7.1-.4L12 8.5l-1.7-1.7A5 5 0 1 0 3.2 14L12 22l8.8-8a5 5 0 0 0 0-6.8z"/></svg>;
    case 'message':     return <svg {...p}><path d="M21 12a8 8 0 0 1-12 7l-5 2 2-5a8 8 0 1 1 15-4z"/></svg>;
    case 'bookmark':    return <svg {...p}><path d="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16l7-4 7 4z"/></svg>;
    case 'filter':      return <svg {...p}><path d="M3 5h18l-7 9v6l-4-2v-4L3 5z"/></svg>;
    case 'menu':        return <svg {...p}><path d="M4 7h16M4 12h16M4 17h16"/></svg>;
    case 'logout':      return <svg {...p}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"/></svg>;
    case 'chart':       return <svg {...p}><path d="M3 21V3M3 21h18"/><rect x="7" y="11" width="3" height="7"/><rect x="13" y="7" width="3" height="11"/><rect x="19" y="14" width="3" height="4"/></svg>;
    case 'chart-bar':   return <svg {...p}><rect x="3" y="14" width="4" height="7"/><rect x="10" y="9" width="4" height="12"/><rect x="17" y="4" width="4" height="17"/><path d="M3 21h18"/></svg>;
    case 'wrench':      return <svg {...p}><path d="M14 7a4 4 0 0 0-4 4l-7 7 3 3 7-7a4 4 0 0 0 5-5l-2 2-2-2 2-2a4 4 0 0 0-2 0z"/></svg>;
    case 'unlock':      return <svg {...p}><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/></svg>;
    case 'refresh':     return <svg {...p}><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>;
    case 'broom':       return <svg {...p}><path d="M14 4l6 6-3 3-3-3M11 7l6 6-7 7H4v-6z"/></svg>;
    case 'truck':       return <svg {...p}><path d="M3 7h11v9H3z"/><path d="M14 10h4l3 3v3h-7"/><circle cx="7" cy="18" r="2"/><circle cx="17" cy="18" r="2"/></svg>;
    case 'lightbulb':   return <svg {...p}><path d="M9 18h6M10 21h4M12 3a6 6 0 0 1 4 10.5V16H8v-2.5A6 6 0 0 1 12 3z"/></svg>;
    case 'key':         return <svg {...p}><circle cx="8" cy="15" r="4"/><path d="m11 12 9-9 2 2-2 2 2 2-3 3-2-2-3 3"/></svg>;
    case 'chevron-right':return <svg {...p}><path d="m9 6 6 6-6 6"/></svg>;
    case 'chevron-down': return <svg {...p}><path d="m6 9 6 6 6-6"/></svg>;
    case 'chevron-left': return <svg {...p}><path d="m15 6-6 6 6 6"/></svg>;
    case 'send':        return <svg {...p}><path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z"/></svg>;
    case 'building':    return <svg {...p}><rect x="4" y="3" width="16" height="18" rx="1"/><path d="M9 8h2M13 8h2M9 12h2M13 12h2M9 16h6"/></svg>;
    case 'briefcase':   return <svg {...p}><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M3 13h18"/></svg>;
    case 'edit':        return <svg {...p}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.1 2.1 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
    case 'trash':       return <svg {...p}><path d="M3 6h18M19 6l-1 14H6L5 6M9 6V4h6v2"/></svg>;
    case 'fire':         return <svg {...p}><path d="M12 2c0 0-5 5-5 10a5 5 0 0 0 10 0c0-5-5-10-5-10z"/><path d="M12 12c0 0-2 2-2 4a2 2 0 0 0 4 0c0-2-2-4-2-4z"/></svg>;
    case 'camera':       return <svg {...p}><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>;
    case 'mic':          return <svg {...p}><rect x="9" y="2" width="6" height="12" rx="3"/><path d="M5 10v1a7 7 0 0 0 14 0v-1"/><path d="M12 18v4M8 22h8"/></svg>;
    case 'mail':         return <svg {...p}><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 7L2 7"/></svg>;
    case 'map-pin':      return <svg {...p}><path d="M12 2a7 7 0 0 0-7 7c0 5 7 13 7 13s7-8 7-13a7 7 0 0 0-7-7z"/><circle cx="12" cy="9" r="2"/></svg>;
    case 'alert':        return <svg {...p}><path d="M10.3 3.3 2 21h20L13.7 3.3a2 2 0 0 0-3.4 0z"/><path d="M12 9v4M12 17h.01"/></svg>;
    case 'image':        return <svg {...p}><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>;
    case 'users':        return <svg {...p}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
    case 'file-text':    return <svg {...p}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="m14 2 6 6h-6M16 13H8M16 17H8M10 9H8"/></svg>;
    case 'qr-code':      return <svg {...p}><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><path d="M14 14h3v3h-3zM20 14h1v1h-1zM14 20h1v1h-1zM17 17h1v1h-1zM20 20h1v1h-1z"/></svg>;
    case 'download':     return <svg {...p}><path d="M12 3v12m0 0 4-4m-4 4-4-4"/><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/></svg>;
    case 'printer':      return <svg {...p}><path d="M6 9V3h12v6"/><rect x="4" y="9" width="16" height="8" rx="1"/><path d="M6 14h12v7H6z"/></svg>;
    case 'nfc':          return <svg {...p}><rect x="3" y="3" width="8" height="18" rx="2"/><path d="M14 8a5 5 0 0 1 0 8"/><path d="M17.5 5a10 10 0 0 1 0 14"/></svg>;
    default:            return <svg {...p}><circle cx="12" cy="12" r="9"/></svg>;
  }
};


export function StatusPill({ status }) {
  const map = { 'Em andamento':{c:'info'}, 'Concluído':{c:'ok'}, 'Aguardando aprovação':{c:'warn'}, 'Pendente':{c:'muted'}, 'Cancelado':{c:'err'} };
  const cls = (map[status]||{c:'muted'}).c;
  return <span className={`status-pill ${cls}`}><span className="ind"/>{status}</span>;
}


export function PhotoPlaceholder({ label, hue=0 }) {
  const patterns = [
    `url("data:image/svg+xml,%3Csvg width='48' height='48' viewBox='0 0 48 48' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h48v1H0zM0 47h48v1H0zM0 0v48h1V0zM47 0v48h1V0z' fill='white'/%3E%3C/svg%3E")`,
    `url("data:image/svg+xml,%3Csvg width='48' height='48' viewBox='0 0 48 48' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='24' cy='24' r='1.5' fill='white'/%3E%3C/svg%3E")`,
    `url("data:image/svg+xml,%3Csvg width='48' height='48' viewBox='0 0 48 48' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 24h48M24 0v48' stroke='white' stroke-width='0.5'/%3E%3C/svg%3E")`,
  ];
  const pat = patterns[Math.abs(hue/18|0) % patterns.length];
  const bg = `linear-gradient(135deg, hsl(${210+hue},38%,18%) 0%, hsl(${220+hue},45%,10%) 100%)`;
  return (
    <div className="slide-bg" style={{ background: bg }}>
      <div className="slide-bg-inner" style={{ backgroundImage: pat }}/>
    </div>
  );
}


export const AUTOPLAY_MS = 6000;


export function Carousel({ slides, mobile=false, onSaibaMais }) {
  const [idx, setIdx]       = useState(0);
  const [paused, setPaused] = useState(false);
  const [progKey, setProgKey] = useState(0); // força re-mount da barra
  const total    = slides.length;
  const timerRef = useRef(null);
  // swipe
  const touchStartX = useRef(null);

  const goTo = (next) => {
    setIdx((next + total) % total);
    setProgKey(k => k + 1);
  };

  // autoplay
  useEffect(() => {
    if (paused) { clearInterval(timerRef.current); return; }
    timerRef.current = setInterval(() => goTo(idx + 1), AUTOPLAY_MS);
    return () => clearInterval(timerRef.current);
  }, [idx, paused, total]);

  // swipe touch
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd   = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) goTo(dx < 0 ? idx + 1 : idx - 1);
    touchStartX.current = null;
  };

  return (
    <div
      className="carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Barra de progresso do autoplay */}
      {!paused && (
        <div className="carousel-progress">
          <div
            key={`${idx}-${progKey}`}
            className="carousel-progress-bar"
            style={{ animationDuration: `${AUTOPLAY_MS}ms` }}
          />
        </div>
      )}

      {/* Track */}
      <div className="carousel-track" style={{ transform:`translateX(-${idx*100}%)` }}>
        {slides.map((s, i) => (
          <div className={`carousel-slide${i===idx?' is-active':''}`} key={s.id}>
            <PhotoPlaceholder label={s.bgKey} hue={i*18} />
            <div className="slide-overlay"/>
            <div className="slide-overlay-bottom"/>
            <div className="slide-content">
              <span className="slide-tag"><span className="tag-dot"/>{s.tag}</span>
              <h3>{s.title}</h3>
              <p>{s.body}</p>
              <button className="slide-cta" onClick={(e) => { e.stopPropagation(); onSaibaMais && onSaibaMais(s); }}>
                Saiba mais
                <Icon name="arrow-right" size={13}/>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Setas laterais (hover, apenas desktop) */}
      {!mobile && <>
        <button className="carousel-arrow left"  onClick={() => goTo(idx-1)}><Icon name="chevron-left"  size={18}/></button>
        <button className="carousel-arrow right" onClick={() => goTo(idx+1)}><Icon name="chevron-right" size={18}/></button>
      </>}

      {/* Rodapé: dots + contador + controles */}
      <div className="carousel-footer">
        <div className="carousel-dots">
          {slides.map((_,i) => (
            <span key={i} className={i===idx?'active':''} onClick={()=>goTo(i)}/>
          ))}
        </div>
        <div className="carousel-actions">
          <span className="carousel-counter">{idx+1}/{total}</span>
          <button className={`carousel-btn pause-btn`} onClick={() => setPaused(p=>!p)} title={paused?'Retomar':'Pausar'}>
            <Icon name={paused ? 'play' : 'pause'} size={13}/>
          </button>
          {!mobile && <>
            <button className="carousel-btn" onClick={()=>goTo(idx-1)}><Icon name="chevron-left"  size={14}/></button>
            <button className="carousel-btn" onClick={()=>goTo(idx+1)}><Icon name="chevron-right" size={14}/></button>
          </>}
        </div>
      </div>
    </div>
  );
}


/* ── FEED / COMUNICADOS ── */
export const COMUNICADO_CATEGORIAS = ['Geral','Operacional','RH','Segurança','Manutenção','Comercial','TI','Evento'];


/* ── NEW REQUEST ── */
/* ── helpers de data para "Quem e Quando" ── */
export function calcMinDate() { return new Date(); }

export function calcMaxDate(from) { const max = new Date(from); max.setDate(max.getDate() + 15); return max; }

export function fmtDateInput(d) {
  return d.toISOString().slice(0,10); // yyyy-mm-dd para input[type=date]
}

/* Lista de horários de 30 em 30 min — 07:00 até 22:00 */
export const TIME_SLOTS = (() => { const s=[]; for(let h=0;h<=23;h++){s.push(String(h).padStart(2,"0")+":00"); if(h<23)s.push(String(h).padStart(2,"0")+":30");} return s; })();

export function TimeSelect({ value, onChange, placeholder="Horário" }) {
  return (
    <select value={value} onChange={e=>onChange(e.target.value)}
      style={{ flex:1, height:38, background:"var(--surface-2)", border:"1px solid var(--line)", borderRadius:8, padding:"0 8px", fontSize:13, color:value?"var(--ink)":"var(--ink-4)", outline:"none", fontFamily:"inherit", cursor:"pointer" }}>
      <option value="">{placeholder}</option>
      {TIME_SLOTS.map(t=><option key={t} value={t}>{t}</option>)}
    </select>
  );
}

export function fmtDateBR(iso) {
  if (!iso) return '—';
  const [y,m,d] = iso.split('-');
  return `${d}/${m}/${y}`;
}



/* ══════════════════════════════════════════════════════════════
   DETALHE DA SOLICITAÇÃO

   O Worker já devolvia histórico e dados do prestador em
   GET /solicitacoes/:id desde a Fase 1, e NENHUMA tela consumia. Quer dizer:
   dava para ver que um chamado foi aprovado, mas não por quem nem quando —
   que é justamente a informação que gera pergunta depois, ainda mais quando
   aprovar libera a entrada de um prestador no shopping.
   ══════════════════════════════════════════════════════════════ */
export function fmtBytes(n) {
  if (!n && n !== 0) return '';
  return n < 1024 * 1024 ? (n / 1024).toFixed(0) + ' KB' : (n / (1024 * 1024)).toFixed(1) + ' MB';
}


export function iconeAnexo(mime) {
  if (mime === 'application/pdf') return '📄';
  if (mime && mime.startsWith('image/')) return '🖼️';
  return '📎';
}


/* ════════════════════════════════════════════════════════════════
   MÓDULO USUÁRIOS
   ════════════════════════════════════════════════════════════════ */
/* A tabela de permissões monta a partir do MESMO catálogo que os Workers
   usam para decidir (shared/permissoes.js, global PERMS).

   Esta lista antes era escrita à mão aqui, e por isso estava errada: faltava
   'home' e faltava 'seguranca'. O módulo de Segurança Patrimonial tinha
   permissão declarada em DEFAULT_PERMISSIONS mas não aparecia nesta tela —
   ou seja, era impossível ajustá-la para qualquer pessoa. Ninguém percebeu
   porque a tela parecia completa. */
export const MODULES = MODULOS_CATALOGO.map(m => ({
  id: m.id, label: m.label, icon: m.icone, group: m.grupo,
  descricao: m.descricao, sub: false,
}));

export const ACTIONS = ACOES_CATALOGO;

export const ACTION_LABELS = ACAO_LABEL;


export const SETORES_USUARIO = [
  'Administração',
  'Marketing',
  'Segurança',
  'Operações',
  'Manutenção/Facilidades',
  'RH/Financeiro',
  'Comercial/Tecnologia',
];


/* ════════════════════════════════════════════════════════════════
   CADASTRO DE LOJAS

   A loja é o tenant desta intranet. Ela responde a duas perguntas que
   aparecem em módulo após módulo:

     • QUEM é a pessoa — o vínculo `usuarios.loja_id`;
     • DE QUEM é o dado — o escopo do GIS é por loja, não por autor.

   Por isso não existe botão de excluir. `gis_vendas`, `gis_semanas` e
   `gis_dias_liberados` referenciam a loja com ON DELETE CASCADE: apagar o
   cadastro levaria junto todo o faturamento histórico dela, sem perguntar
   nada. Loja que saiu do shopping se DESATIVA — some dos seletores e o
   histórico continua de pé, que é o que permite comparar o ano passado.
   ════════════════════════════════════════════════════════════════ */
export const SEGMENTOS_LOJA = [
  'Alimentação', 'Vestuário', 'Calçados', 'Acessórios', 'Beleza & Saúde',
  'Casa & Decoração', 'Eletrônicos', 'Serviços', 'Lazer & Entretenimento',
  'Quiosque', 'Administrativo', 'Outro',
];

export const PISOS_LOJA = ['Térreo', 'Piso 1', 'Piso 2', 'Piso 3', 'Subsolo', 'Externo'];


/* ════════════════════════════════════════════════════════════════
   MÓDULO RH — CADASTRAR VAGA DE EMPREGO
   ════════════════════════════════════════════════════════════════ */
export const CARGOS_VAGA = [
  'Ajudante de Cozinha','Alface','Analista de Crédito','Analista de RH','Assistente de Loja',
  'Assistente Jurídico','Atendente','Atendimento no Provador','Auditor(a)','Auxiliar de Auditoria',
  'Auxiliar de Crédito','Auxiliar de Empacotamento','Auxiliar de Escritório','Auxiliar de Estoque',
  'Auxiliar de Limpeza','Balconista','Bombeiro Civil','Caixa','Coordenador','Cozinheiros(as)',
  'Costureira','Eletricista','Encanador','Estagiário(a)','Fiscal de Loja','Gerente de Loja',
  'Informática (T.I.)','Líder de Turno','Marceneiro','Monitor(a) de Loja','Montador de Móveis',
  'Office-boy','Operador(a) de Caixa','Outros','Pizzaiolo','Promotor(a) de Vendas','Recepcionista',
  'Sushiman','Técnico de Manutenção','Telefonista','Vendedor(a)','Vendedor(a) de Artigos Esportivos',
  'Vendedor(a) de Calçados, Bolsas e Couro','Vendedor(a) de Celulares','Vendedor(a) de Cine, Foto e Ótica',
  'Vendedor(a) de Eletrodomésticos','Vendedor(a) de Eletroeletrônicos','Vendedor(a) de Moda Feminina',
  'Vendedor(a) de Moda Jovem','Vendedor(a) de Moda Masculina','Vendedor(a) de Moda SurfWear / SkateWear',
  'Vendedor(a) de Presentes',
];


/* ════════════════════════════════════════════════════════════════
   MÓDULO RH — CONSULTAR CURRÍCULOS
   ════════════════════════════════════════════════════════════════ */
export const ESCOLARIDADES = [
  'Doutorado Completo','Doutorado Incompleto','Mestrado Completo','Mestrado Incompleto',
  'Pós-graduação Completa','Pós-graduação Incompleta','Superior Completo','Superior Incompleto',
  'Ensino Médio Completo','Ensino Médio Incompleto','Ensino Fundamental Completo','Ensino Fundamental Incompleto',
  'Não Alfabetizado',
];

export const ESTADO_CIVIL = ['Casado(a)','Desquitado(a)','Divorciado(a)','Separado(a)','Solteiro(a)','Viúvo(a)'];

export const CARGOS_CURRICULO = [
  'Ajudante de Cozinha','Alface','Analista de Crédito','Analista de RH','Assistente de Loja',
  'Assistente Jurídico','Atendente','Atendimento no Provador','Auditor(a)','Auxiliar de Auditoria',
  'Auxiliar de Crédito','Auxiliar de Empacotamento','Auxiliar de Escritório','Auxiliar de Estoque',
  'Auxiliar de Limpeza','Balconista','Bombeiro Civil','Caixa','Coordenador','Cozinheiros(as)',
  'Costureira','Eletricista','Encanador','Estagiário(a)','Fiscal de Loja','Gerente de Loja',
  'Informática (T.I.)','Líder de Turno','Marceneiro','Monitor(a) de Loja','Montador de Móveis',
  'Office-boy','Operador(a) de Caixa','Outros','Pizzaiolo','Promotor(a) de Vendas','Recepcionista',
  'Sushiman','Técnico de Manutenção','Telefonista','Vendedor(a)','Vendedor(a) de Artigos Esportivos',
  'Vendedor(a) de Calçados, Bolsas e Couro','Vendedor(a) de Celulares','Vendedor(a) de Cine, Foto e Ótica',
  'Vendedor(a) de Eletrodomésticos','Vendedor(a) de Eletroeletrônicos','Vendedor(a) de Moda Feminina',
  'Vendedor(a) de Moda Jovem','Vendedor(a) de Moda Masculina','Vendedor(a) de Moda SurfWear / SkateWear',
  'Vendedor(a) de Presentes',
];


export function CheckboxGroup({ label, items, selected, onChange }) {
  const toggle = item => onChange(selected.includes(item) ? selected.filter(x=>x!==item) : [...selected,item]);
  return (
    <div style={{ marginBottom:16 }}>
      <div style={{ fontSize:11,fontWeight:600,color:'var(--ink-3)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:8,padding:'4px 0',borderBottom:'1px solid var(--line)' }}>{label}</div>
      <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:6 }}>
        {items.map(item=>(
          <label key={item} style={{ display:'flex',alignItems:'center',gap:7,fontSize:12,color:'var(--ink-2)',cursor:'pointer',padding:'3px 0' }}>
            <input type="checkbox" checked={selected.includes(item)} onChange={()=>toggle(item)} style={{ accentColor:'var(--navy)',width:14,height:14 }}/>
            {item}
          </label>
        ))}
      </div>
    </div>
  );
}


/* Rótulo/cor do status — usado aqui e na tabela de resultados. `recebido` é
   o estado inicial (nem toda candidatura chega a ser analisada). */
export const STATUS_CURRICULO = {
  recebido:    { label:'Recebido',    classe:'muted' },
  em_analise:  { label:'Em análise',  classe:'info'  },
  aprovado:    { label:'Aprovado',    classe:'ok'    },
  reprovado:   { label:'Reprovado',   classe:'err'   },
};


/* ── Máscaras de input ── */
export function maskCep(v) {
  const d = v.replace(/\D/g,'').slice(0,8);
  if (d.length > 5) return d.slice(0,5)+'-'+d.slice(5);
  return d;
}

export function maskPhone(v) {
  // Padrão único: (XX) XXXXX-XXXX  (11 dígitos — celular)
  const d = v.replace(/\D/g,'').slice(0,11);
  if (d.length === 0) return '';
  if (d.length <= 2)  return '(' + d;
  if (d.length <= 7)  return '(' + d.slice(0,2) + ') ' + d.slice(2);
  return '(' + d.slice(0,2) + ') ' + d.slice(2,7) + '-' + d.slice(7);
}

export function maskCpf(v) {
  const d = v.replace(/\D/g,'').slice(0,11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return d.slice(0,3) + '.' + d.slice(3);
  if (d.length <= 9) return d.slice(0,3) + '.' + d.slice(3,6) + '.' + d.slice(6);
  return d.slice(0,3) + '.' + d.slice(3,6) + '.' + d.slice(6,9) + '-' + d.slice(9);
}


/* ════════════════════════════════════════════════════════════════
   PAINEL DE VAGAS — CANDIDATAR A VAGA (cadastro de currículo)
   ════════════════════════════════════════════════════════════════ */
export const ESTADOS_BR = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];


/* ════════════════════════════════════════════════════════════════
   MÓDULO GIS — DECLARAÇÃO DE VENDAS DIÁRIAS (Lojista)
   ════════════════════════════════════════════════════════════════ */

export function fmtBRL(val) {
  if (val === null || val === undefined || val === '') return '';
  const n = parseFloat(String(val).replace(',', '.'));
  if (isNaN(n)) return '';
  return n.toLocaleString('pt-BR', { style:'currency', currency:'BRL' });
}


export function parseBRL(str) {
  if (!str || str.trim() === '') return null;
  const cleaned = str.replace(/[R$\s]/g,'').replace('.','').replace(',','.');
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : n;
}


/* getWeekKey() foi removida: a semana passou a ser identificada pelo índice
   numérico (0..5) dentro do mês, que é o que o servidor guarda em
   `gis_semanas.semana_idx`. A string `gis_ANO_MES_wN` existia só para ser
   chave de um objeto no localStorage, e obrigava a desmontar o texto toda vez
   que alguém precisava saber de que semana se tratava. */

export function getDayKey(year, month, day) {
  return `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
}


export function buildCalendar(year, month) {
  const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev  = new Date(year, month, 0).getDate();
  const weeks = [];
  let day = 1;
  let prevDay = daysInPrev - firstDay + 1;

  while (day <= daysInMonth) {
    const week = [];
    for (let d = 0; d < 7; d++) {
      if (weeks.length === 0 && d < firstDay) {
        week.push({ day: prevDay + d, outside: true, month: month - 1 });
      } else if (day > daysInMonth) {
        week.push({ day: day - daysInMonth, outside: true, month: month + 1 });
        day++;
      } else {
        week.push({ day, outside: false, month });
        day++;
      }
    }
    weeks.push(week);
  }
  return weeks;
}


export function Blocked() {
  return <div className="main-pad"><div className="card" style={{ textAlign:'center', padding:40, color:'var(--ink-3)' }}>
    <Icon name="shield" size={32} style={{ margin:'0 auto 12px', display:'block' }}/>
    <p>Você não tem permissão para acessar este módulo.</p>
  </div></div>;
}



/* ════════════════════════════════════════════════════════════════
   SEGURANÇA MODULE — Ativos, Grupos de Controle, Vistorias, Dashboard

   Migrado do localStorage para o worker `intranet-osasco-seg` (Fase 3). A
   vistoria é histórico com valor legal — em caso de sinistro é o registro
   que mostra se o shopping cumpriu o prazo — por isso o worker só aceita
   INSERT nela, nunca UPDATE ou DELETE de conteúdo.

   A foto vem do R2 por trás de autorização (GET /seg/vistoria/:id/foto),
   não mais em base64 solto no JSON sincronizado pelo GitHub. `SegFoto`, logo
   abaixo, é o componente que busca a foto como blob autenticado e libera a
   memória quando sai da tela — é o mesmo padrão de download de anexo que
   Solicitações já usa, adaptado para pré-visualização em vez de salvar.
   ════════════════════════════════════════════════════════════════ */

/* ── Helpers ── */
/* Quando `dataValidade` está preenchida (ex: vencimento da recarga de um
   extintor), ela manda no status — independe de vistoria registrada — e o
   limiar de "próximo do vencimento" é o `avisoDias` do próprio ativo (padrão
   30). Sem `dataValidade`, cai no comportamento de sempre: `proximaVistoria`
   com limiar fixo de 30 dias. Ver DOC/ADR/0012 — os dois campos convivem de
   propósito, um não substitui o outro. */
export function segDueStatus(asset) {
  const dataRef = asset.dataValidade || asset.proximaVistoria;
  if (!dataRef) return { cls:'muted', label:'Sem data' };
  const limiar = asset.dataValidade ? (asset.avisoDias || 30) : 30;
  const now = Date.now();
  const due = new Date(dataRef).getTime();
  const diff = due - now;
  const dias = Math.ceil(diff / 86400000);
  if (dias < 0)  return { cls:'err',  label:`Vencido há ${Math.abs(dias)}d` };
  if (dias <= limiar) return { cls:'warn', label:`Vence em ${dias}d` };
  return { cls:'ok', label:`${dias}d restantes` };
}


export function fmtDate(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleDateString('pt-BR');
}


/* Só uso client-side: pré-preenche o campo "Próxima vistoria" com uma
   sugestão editável antes de enviar. O valor que realmente grava é
   recalculado no servidor (ver seg-worker `proximaData`), então divergir
   aqui não corrompe nada — na pior hipótese a pessoa vê uma sugestão errada
   e corrige antes de confirmar. */
export function calcProxima(dataBase, prazo, unidade = 'meses') {
  const d = new Date(dataBase);
  if (unidade === 'dias') d.setDate(d.getDate() + prazo);
  else d.setMonth(d.getMonth() + prazo);
  return d.toISOString().split('T')[0];
}
